/**
 * OWLY CRM - Mock Users for Testing
 *
 * Usuarios de prueba con diferentes roles y asignaciones de proyectos
 * Incluye roles legacy y nuevos roles SGI
 */

import { UserRole as SGIUserRole } from '../types/sgi-roles.types';

// Legacy role type (mantener compatibilidad)
export type LegacyUserRole = 'president' | 'manager' | 'agent';

// Combined role type
export type UserRole = LegacyUserRole;

export interface MockUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  sgiRole?: SGIUserRole; // Nuevo campo para roles SGI
  companyId?: string; // ID de compañía (para multi-tenancy)
  assignedProjects: string[]; // Array de project IDs
  avatar?: string;
  jobTitle?: string; // Cargo/posición (ej: "CEO", "CTO", "Head of Sales")
}

export const MOCK_USERS: MockUser[] = [
  // ========== ROLES INTERNOS DE OWLY ==========
  // Estructura organizacional realista para SaaS B2B en etapa de growth

  // === 1. NIVEL EJECUTIVO (C-LEVEL) ===

  // CEO - Liderazgo estratégico y financiero
  {
    id: 'user-owly-ceo',
    name: 'Pablo Gonzalez',
    email: 'pablo.gonzalez@owly.cl',
    role: 'president',
    sgiRole: SGIUserRole.OWLY_ADMIN,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'CEO',
  },

  // CTO - Infraestructura técnica y monitoring
  {
    id: 'user-owly-cto',
    name: 'Santiago Serna',
    email: 'santiago.serna@owly.cl',
    role: 'president',
    sgiRole: SGIUserRole.OWLY_SUPER_ADMIN,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'CTO',
  },

  // === 2. REVENUE & GROWTH (GO-TO-MARKET) ===

  // Customer Success Manager - Onboarding y retención
  {
    id: 'user-owly-csm',
    name: 'Andres Martinez',
    email: 'andres.martinez@owly.cl',
    role: 'manager',
    sgiRole: SGIUserRole.OWLY_SALES,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'Customer Success Manager',
  },

  // === 3. PRODUCTO & MARKETING ===

  // Product Manager - Roadmap y features
  {
    id: 'user-owly-product',
    name: 'Juan Numpaque',
    email: 'juan.numpaque@owly.cl',
    role: 'manager',
    sgiRole: SGIUserRole.OWLY_SUPPORT,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'Product Manager',
  },

  // Marketing Manager - Adquisición y brand
  {
    id: 'user-owly-marketing',
    name: 'Natalia Sanchez',
    email: 'natalia.sanchez@owly.cl',
    role: 'manager',
    sgiRole: SGIUserRole.OWLY_SALES,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'Marketing Manager',
  },

  // === 4. ENGINEERING & DEVOPS ===

  // Backend Developer - APIs e integraciones
  {
    id: 'user-owly-backend',
    name: 'Camilo Quintero',
    email: 'camilo.quintero@owly.cl',
    role: 'agent',
    sgiRole: SGIUserRole.OWLY_DEV,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'Backend Developer',
  },

  // Frontend Developer - UI/UX y componentes
  {
    id: 'user-owly-frontend',
    name: 'Nicolas Ayala',
    email: 'nicolas.ayala@owly.cl',
    role: 'agent',
    sgiRole: SGIUserRole.OWLY_DEV,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'Frontend Developer',
  },

  // === 5. FINANCE ===

  // CFO - Gestión financiera y billing
  {
    id: 'user-owly-cfo',
    name: 'Manuela Gamboa',
    email: 'manuela.gamboa@owly.cl',
    role: 'president',
    sgiRole: SGIUserRole.OWLY_FINANCE,
    assignedProjects: [],
    avatar: '🦉',
    jobTitle: 'CFO',
  },

  // === 6. AI AGENTS ===

  // AI Customer Success Agent
  {
    id: 'user-owly-ai-cs',
    name: 'OWLY AI Agent',
    email: 'ai.agent@owly.cl',
    role: 'manager',
    sgiRole: SGIUserRole.OWLY_CUSTOMER_SUCCESS,
    assignedProjects: [],
    avatar: '🤖',
    jobTitle: 'AI Customer Success Agent',
  },

  // ========== SGI ROLES (CLIENTES) ==========

  // INMOBILIARIA LOS ANDES (comp-001) - Icono: 🏢
  {
    id: 'user-losandes-admin',
    name: 'Ricardo Valenzuela',
    email: 'ricardo.valenzuela@losandes.cl',
    role: 'president',
    sgiRole: SGIUserRole.COMPANY_ADMIN,
    companyId: 'comp-001',
    assignedProjects: [], // Acceso a todos los proyectos de su compañía
    avatar: '🏢',
  },
  {
    id: 'user-losandes-pm',
    name: 'Patricia Morales',
    email: 'patricia.morales@losandes.cl',
    role: 'manager',
    sgiRole: SGIUserRole.PROJECT_MANAGER,
    companyId: 'comp-001',
    assignedProjects: ['torre-verde', 'bosques-del-rio'], // Torre Verde, Bosques del Río
    avatar: '🏢',
  },
  {
    id: 'user-losandes-sales-manager',
    name: 'Felipe Gómez',
    email: 'felipe.gomez@losandes.cl',
    role: 'manager',
    sgiRole: SGIUserRole.SALES_MANAGER,
    companyId: 'comp-001',
    assignedProjects: [],
    avatar: '🏢',
  },
  {
    id: 'user-losandes-agent-1',
    name: 'Carmen López',
    email: 'carmen.lopez@losandes.cl',
    role: 'agent',
    sgiRole: SGIUserRole.SALES_AGENT,
    companyId: 'comp-001',
    assignedProjects: ['torre-verde'], // Torre Verde
    avatar: '🏢',
  },
  {
    id: 'user-losandes-agent-2',
    name: 'Daniela Rojas',
    email: 'daniela.rojas@losandes.cl',
    role: 'agent',
    sgiRole: SGIUserRole.SALES_AGENT,
    companyId: 'comp-001',
    assignedProjects: ['bosques-del-rio'], // Bosques del Río
    avatar: '🏢',
  },

  // CONSTRUCTORA PACIFICO (comp-002) - Icono: 🏗️
  {
    id: 'user-pacifico-admin',
    name: 'Andrés Castillo',
    email: 'andres.castillo@constructorapacifico.cl',
    role: 'president',
    sgiRole: SGIUserRole.COMPANY_ADMIN,
    companyId: 'comp-002',
    assignedProjects: [],
    avatar: '🏗️',
  },
  {
    id: 'user-pacifico-pm',
    name: 'Mónica Gutiérrez',
    email: 'monica.gutierrez@constructorapacifico.cl',
    role: 'manager',
    sgiRole: SGIUserRole.PROJECT_MANAGER,
    companyId: 'comp-002',
    assignedProjects: ['ciudad-jardin'], // Ciudad Jardín
    avatar: '🏗️',
  },
  {
    id: 'user-pacifico-agent-1',
    name: 'Jorge Peña',
    email: 'jorge.pena@constructorapacifico.cl',
    role: 'agent',
    sgiRole: SGIUserRole.SALES_AGENT,
    companyId: 'comp-002',
    assignedProjects: ['ciudad-jardin'], // Ciudad Jardín
    avatar: '🏗️',
  },

  // PROPIEDADES PREMIUM (comp-005) - Icono: 💎
  {
    id: 'user-premium-admin',
    name: 'Claudia Vargas',
    email: 'claudia.vargas@propiedadespremium.cl',
    role: 'president',
    sgiRole: SGIUserRole.COMPANY_ADMIN,
    companyId: 'comp-005',
    assignedProjects: [],
    avatar: '💎',
  },
  {
    id: 'user-premium-sales-manager',
    name: 'Rodrigo Sepúlveda',
    email: 'rodrigo.sepulveda@propiedadespremium.cl',
    role: 'manager',
    sgiRole: SGIUserRole.SALES_MANAGER,
    companyId: 'comp-005',
    assignedProjects: [],
    avatar: '💎',
  },

  // DESARROLLOS URBANOS SPA (comp-007) - Icono: 🏙️
  {
    id: 'user-urbanos-admin',
    name: 'Fernando Soto',
    email: 'fernando.soto@desarrollosurbanos.cl',
    role: 'president',
    sgiRole: SGIUserRole.COMPANY_ADMIN,
    companyId: 'comp-007',
    assignedProjects: [],
    avatar: '🏙️',
  },
  {
    id: 'user-urbanos-pm',
    name: 'Isabel Ramírez',
    email: 'isabel.ramirez@desarrollosurbanos.cl',
    role: 'manager',
    sgiRole: SGIUserRole.PROJECT_MANAGER,
    companyId: 'comp-007',
    assignedProjects: [],
    avatar: '🏙️',
  },

];

// Helper para obtener usuario por ID
export const getUserById = (userId: string): MockUser | undefined => {
  return MOCK_USERS.find(user => user.id === userId);
};

// Helper para obtener usuarios por rol
export const getUsersByRole = (role: UserRole): MockUser[] => {
  return MOCK_USERS.filter(user => user.role === role);
};

// Helper para obtener agentes de un proyecto
export const getAgentsByProject = (projectId: string): MockUser[] => {
  return MOCK_USERS.filter(
    user => user.role === 'agent' && user.assignedProjects.includes(projectId)
  );
};

// Helper para verificar si un usuario tiene acceso a un proyecto
export const hasProjectAccess = (user: MockUser, projectId: string): boolean => {
  // President y Manager tienen acceso a todo
  if (user.role === 'president' || user.role === 'manager') {
    return true;
  }

  // Otros roles solo tienen acceso a sus proyectos asignados
  return user.assignedProjects.includes(projectId);
};

// Usuario por defecto para testing
// Usar PROJECT_MANAGER de una compañía cliente para testing realista
export const DEFAULT_TEST_USER = MOCK_USERS.find(u => u.id === 'user-losandes-pm') || MOCK_USERS[0]; // Patricia Morales - PROJECT_MANAGER (Los Andes)

// Helper para obtener usuarios SGI
export const getSGIUsersByRole = (sgiRole: SGIUserRole): MockUser[] => {
  return MOCK_USERS.filter(user => user.sgiRole === sgiRole);
};

// Helper para obtener usuarios por compañía
export const getUsersByCompany = (companyId: string): MockUser[] => {
  return MOCK_USERS.filter(user => user.companyId === companyId);
};

/**
 * RESUMEN DE USUARIOS MOCK:
 *
 * ========== ESTRUCTURA ORGANIZACIONAL OWLY (9 usuarios) - 🦉 y 🤖 ==========
 *
 * Equipo core de OWLY para gestión de plataforma SaaS B2B
 *
 * === 1. NIVEL EJECUTIVO (C-LEVEL) - 3 usuarios ===
 *
 * CEO - Pablo Gonzalez (role: president, sgiRole: OWLY_ADMIN)
 * - Visión: MRR, ARR, churn rate, crecimiento de clientes, health score
 * - Dashboard: KPIs financieros y estratégicos
 *
 * CTO - Santiago Serna (role: president, sgiRole: OWLY_SUPER_ADMIN)
 * - Visión: Uptime, performance, logs de errores, deployments, recursos
 * - Dashboard: Monitoring técnico, infraestructura, roadmap tecnológico
 *
 * CFO - Manuela Gamboa (role: president, sgiRole: OWLY_FINANCE)
 * - Visión: Facturación, cobros, MRR, ARR, análisis financiero
 * - Dashboard: KPIs financieros, billing, revenue analytics
 *
 * === 2. REVENUE & GROWTH - 1 usuario ===
 *
 * Customer Success Manager - Andres Martinez (role: manager, sgiRole: OWLY_SALES)
 * - Visión: Onboarding, adoption rate, tickets de soporte, riesgo de churn
 * - Acciones: Health checks, expansión de cuentas, renovaciones
 *
 * === 3. PRODUCTO & MARKETING - 2 usuarios ===
 *
 * Product Manager - Juan Numpaque (role: manager, sgiRole: OWLY_SUPPORT)
 * - Visión: Roadmap, feature requests, feedback de clientes, usage analytics
 * - Acciones: Priorización de features, beta testing, changelog
 *
 * Marketing Manager - Natalia Sanchez (role: manager, sgiRole: OWLY_SALES)
 * - Visión: Adquisición de leads, CAC, brand awareness, content performance
 * - Acciones: Campañas, website, SEO, materiales de ventas
 *
 * === 4. ENGINEERING - 2 usuarios ===
 *
 * Backend Developer - Camilo Quintero (role: agent, sgiRole: OWLY_DEV)
 * - Visión: APIs, integraciones, performance de queries, webhooks
 * - Acciones: Nuevas features, optimizaciones, integraciones con terceros
 *
 * Frontend Developer - Nicolas Ayala (role: agent, sgiRole: OWLY_DEV)
 * - Visión: UI/UX, bugs reportados, tiempo de carga, experiencia de usuario
 * - Acciones: Mejoras de interfaz, componentes, accesibilidad
 *
 * === 5. AI AGENTS - 1 usuario ===
 *
 * AI Customer Success Agent - OWLY AI Agent (role: manager, sgiRole: OWLY_CUSTOMER_SUCCESS)
 * - Visión: Respuestas automáticas, onboarding, health score, retención
 * - Acciones: Chat 24/7, alertas de churn, automatización de CS
 *
 * RESUMEN DE ROLES BASE:
 * - 3 Presidents: CEO + CTO + CFO (acceso total / financiero)
 * - 4 Managers: CS, Product, Marketing, AI Agent (gestión)
 * - 2 Agents: Backend Dev, Frontend Dev (ejecución)
 *
 * ========== SGI ROLES (CLIENTES) - 12 usuarios ==========
 * Cada compañía tiene su propio icono compartido entre todos sus usuarios
 *
 * 🏢 INMOBILIARIA LOS ANDES (@losandes.cl) - 5 usuarios:
 * - Ricardo Valenzuela: COMPANY_ADMIN (role: president) - Director General
 * - Patricia Morales: PROJECT_MANAGER (role: manager) - Torres del Sol y Vista Mar
 * - Felipe Gómez: SALES_MANAGER (role: manager) - Gerente Comercial
 * - Carmen López: SALES_AGENT (role: agent) - Torres del Sol
 * - Daniela Rojas: SALES_AGENT (role: agent) - Vista Mar
 *
 * 🏗️ CONSTRUCTORA PACIFICO (@constructorapacifico.cl) - 3 usuarios:
 * - Andrés Castillo: COMPANY_ADMIN (role: president) - Gerente General
 * - Mónica Gutiérrez: PROJECT_MANAGER (role: manager) - Sky Tower
 * - Jorge Peña: SALES_AGENT (role: agent) - Sky Tower
 *
 * 💎 PROPIEDADES PREMIUM (@propiedadespremium.cl) - 2 usuarios:
 * - Claudia Vargas: COMPANY_ADMIN (role: president) - CEO
 * - Rodrigo Sepúlveda: SALES_MANAGER (role: manager) - Jefe de Ventas
 *
 * 🏙️ DESARROLLOS URBANOS SPA (@desarrollosurbanos.cl) - 2 usuarios:
 * - Fernando Soto: COMPANY_ADMIN (role: president) - Director
 * - Isabel Ramírez: PROJECT_MANAGER (role: manager) - Gestora de Proyectos
 *
 * ========== ESTRUCTURA DE ROLES ==========
 * Cada usuario tiene 2 niveles de roles:
 * 1. role (Base): president, manager, agent - Para compatibilidad con dashboards legacy
 * 2. sgiRole (Específico): OWLY_ADMIN, COMPANY_ADMIN, PROJECT_MANAGER, etc. - Para permisos SGI
 *
 * TOTAL: 21 usuarios (9 OWLY internos + 12 clientes SGI)
 *
 * ICONOS POR ORGANIZACIÓN:
 * 🦉 = Equipo OWLY interno humano (8 usuarios)
 * 🤖 = AI Agents OWLY (1 usuario)
 * 🏢 = Inmobiliaria Los Andes (5 usuarios)
 * 🏗️ = Constructora Pacifico (3 usuarios)
 * 💎 = Propiedades Premium (2 usuarios)
 * 🏙️ = Desarrollos Urbanos SpA (2 usuarios)
 *
 * NOTA: Se eliminaron los usuarios legacy (8 usuarios) porque eran redundantes.
 * Todos los usuarios nuevos tienen el campo "role" para compatibilidad con la app existente.
 */
